import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useState } from "react";

/**
 * How long to keep waiting for the gate data before deciding it is never coming.
 *
 * Bounded on purpose: without it, a browser that cannot reach LaunchDarkly at all (an ad
 * blocker, a locked-down clinic network) or a permanently failing `/auth/users/me/` would
 * hold a fallback forever, which is worse than the redirect this exists to prevent. The
 * real case — the payload landing a beat after the first render — settles in well under a
 * second, so this only runs out when something is genuinely wrong.
 *
 * Matches FLAG_DELIVERY_GRACE_MS in use-sales-coach-route-access.hook.ts.
 */
export const ROUTE_GATE_GRACE_MS = 3_000;

/**
 * Whether the data that decides which routes exist has arrived yet — so a caller can tell
 * "this URL matches nothing" from "nothing has loaded yet".
 *
 * **Why the LaunchDarkly half is not `flags[SOME_KEY] !== undefined`.** LaunchDarkly
 * delivers a context's entire flag set in one go, so an empty object means "nothing has
 * arrived" while a populated one missing your key means the flag genuinely does not exist
 * for this context — which is correctly off. Reading a single key cannot tell those apart,
 * and treating "haven't heard yet" as "off" is the bug this exists to prevent: on a cold
 * load of a deep link, a route gated on `flags[KEY] ?? false` is unregistered at first
 * render, the router's catch-all matches instead, and a `<Navigate replace>` permanently
 * rewrites the URL the user actually asked for.
 *
 * **`additionalGatesResolved` is for the gates LaunchDarkly does not own** — roles,
 * `Practice` attributes, anything off `/auth/users/me/`. Pass `false` while they are still
 * loading. It is an argument rather than a second hook because the two must share ONE
 * deadline: the app runs LaunchDarkly with `bootstrap: "localStorage"`, so on a warm
 * profile the flags are already there on the first render while `/me` is still in flight —
 * two independent 3s waits would let a page sit for six seconds on the exact screen this
 * is meant to keep responsive.
 *
 * Same bug class as ENG-664 (Collections), ENG-1668 (Action Items) and the Sales Coach
 * section, all fixed in `routes/routes.tsx`. `useSalesCoachRouteAccess` answers the
 * narrower "is this one flag on" question for a single section.
 *
 * Returns `true` once the gate data has landed, or once the grace period is up — never
 * blocks the UI indefinitely.
 */
export default function useRouteGateReady(
  additionalGatesResolved: boolean = true
): boolean {
  const flags = useFlags();
  const isResolved =
    Object.keys(flags).length > 0 && Boolean(additionalGatesResolved);

  const [graceExpired, setGraceExpired] = useState(false);

  useEffect(() => {
    if (isResolved) return undefined;
    const timer = setTimeout(() => setGraceExpired(true), ROUTE_GATE_GRACE_MS);
    return () => clearTimeout(timer);
  }, [isResolved]);

  return isResolved || graceExpired;
}
